import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { cn } from "@/lib/utils";
import { SistLogo } from "@/components/common/SistLogo";
import FotoAnggota from "./FotoAnggota";

const JUDUL = {
  siswa: "Kartu Pelajar",
  guru: "Kartu Guru",
  wali_kelas: "Kartu Guru & Wali Kelas",
  operator: "Kartu Pegawai ASN",
  pegawai_non_asn: "Kartu Pegawai Non-ASN",
  kepala_sekolah: "Kartu Kepala Sekolah",
  petugas_presensi: "Kartu Petugas Presensi",
  school_admin: "Kartu Pengurus Sekolah",
  orang_tua: "Kartu Orang Tua/Wali",
};

const TEMA = {
  siswa: ["#1E56C8", "#0B3D91"],
  guru: ["#0F766E", "#0B4A45"],
  wali_kelas: ["#0F766E", "#0B4A45"],
  operator: ["#B45309", "#78350F"],
  pegawai_non_asn: ["#6D28D9", "#4C1D95"],
  kepala_sekolah: ["#1F2937", "#0B1120"],
  petugas_presensi: ["#0B3D91", "#06224F"],
  school_admin: ["#0B3D91", "#06224F"],
  orang_tua: ["#475569", "#1E293B"],
};

const ROLE_LABEL = {
  siswa: "Siswa", guru: "Guru", wali_kelas: "Guru & Wali Kelas", operator: "Pegawai ASN",
  pegawai_non_asn: "Pegawai Non-ASN", kepala_sekolah: "Kepala Sekolah", petugas_presensi: "Petugas Presensi",
  school_admin: "Pengurus Sekolah", orang_tua: "Orang Tua/Wali",
};

const LEVEL = { SD: "Sekolah Dasar", SMP: "Sekolah Menengah Pertama", SMA: "Sekolah Menengah Atas", SMK: "Sekolah Menengah Kejuruan" };

const tanggalID = (iso) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric" })
      .format(new Date(iso));
  } catch { return null; }
};

export const buatQr = (teks) => QRCode.toDataURL(teks, {
  margin: 0, width: 420, errorCorrectionLevel: "M", color: { dark: "#0B1120FF", light: "#FFFFFFFF" },
});

const Lambang = ({ nama, warna }) => {
  const inisial = String(nama || "SIST").split(/\s+/).filter(Boolean).slice(0, 3).map((k) => k[0]).join("").toUpperCase();
  return (
    <svg viewBox="0 0 40 44" className="h-[7.2mm] w-[6.5mm] shrink-0" aria-hidden>
      <path d="M20 1.5 L37.5 8 V24 C37.5 33 29.5 39.5 20 42.5 C10.5 39.5 2.5 33 2.5 24 V8 Z" fill="#FFFFFF" />
      <path d="M20 4 L35 9.7 V24 C35 31.5 28 37.2 20 39.8 C12 37.2 5 31.5 5 24 V9.7 Z" fill={warna} />
      <text x="20" y="26.5" textAnchor="middle" fontSize="12.5" fontWeight="800" fill="#FFFFFF" fontFamily="inherit">{inisial}</text>
    </svg>
  );
};

const Baris = ({ label, nilai }) => nilai && (
  <div className="flex items-baseline gap-[1.3mm]">
    <span className="w-[12.5mm] shrink-0 text-[4pt] font-semibold uppercase tracking-[0.03em] text-slate-500">{label}</span>
    <span className="min-w-0 truncate text-[5.9pt] font-bold leading-[2.8mm] text-slate-900">{nilai}</span>
  </div>
);

/** Kartu ID portrait 54 × 86 mm (kartu PVC diputar) — dipakai di layar, lembar cetak A4, dan berkas PNG. */
export const KartuID = ({ kartu, sekolah, qr, className, testId = "kartu-id" }) => {
  const a = kartu?.anggota || {};
  const [gelap, terang] = TEMA[a.peran] || TEMA.orang_tua;
  const [src, setSrc] = useState(null);
  const tautan = kartu?.tautan;

  useEffect(() => {
    let aktif = true;
    if (qr) { setSrc(qr); return undefined; }
    if (!tautan) { setSrc(null); return undefined; }
    buatQr(tautan).then((url) => { if (aktif) setSrc(url); }).catch(() => {});
    return () => { aktif = false; };
  }, [qr, tautan]);

  const pegawai = a.peran !== "siswa";
  // Alamat sekolah sering sudah memuat kota/provinsi, jadi bagian yang sama tidak dicetak dua kali.
  const bagianAlamat = [sekolah?.address, sekolah?.city, sekolah?.province].filter(Boolean);
  const alamat = bagianAlamat
    .filter((bagian, i) => !bagianAlamat.slice(0, i).some((awal) => awal.toLowerCase().includes(bagian.toLowerCase())))
    .join(", ");
  const identitas = pegawai ? "NIP" : "NISN";
  const nilaiIdentitas = pegawai ? a.nip : a.nisn;
  // Jabatan hanya dicetak bila menambah info di luar label peran di bawah nama.
  const jabatan = a.kelas ? (pegawai ? `Wali Kelas ${a.kelas}` : `Kelas ${a.kelas}`) : "";
  const registrasi = a.registrasi && a.registrasi !== nilaiIdentitas ? a.registrasi : null;
  const dataKartu = [
    [identitas, nilaiIdentitas],
    [pegawai ? "Jabatan" : "Kelas", jabatan],
    ["No. Reg", registrasi],
    ["Tgl. Lahir", tanggalID(a.tanggal_lahir)],
    ["Berlaku s/d", tanggalID(kartu?.berlaku_hingga)],
  ].filter(([, nilai]) => nilai);

  return (
    <div className={cn("sist-kartu-id relative flex h-[86mm] w-[54mm] flex-col justify-between overflow-hidden rounded-[2.2mm] bg-white text-slate-900 shadow-lg ring-1 ring-black/10", className)}
      data-testid={testId}>
      <header className="shrink-0 px-[2.3mm] pt-[1.9mm] pb-[1.5mm]" style={{ background: `linear-gradient(135deg, ${gelap}, ${terang})` }}>
        <div className="flex items-center gap-[1.7mm]">
          <Lambang nama={sekolah?.name} warna={terang} />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 min-h-[6mm] text-[7pt] font-extrabold uppercase leading-[3mm] tracking-[-0.005em] text-white">
              {sekolah?.name || "Sekolah"}
            </p>
            <p className="truncate text-[3.9pt] font-semibold uppercase leading-[1.7mm] tracking-[0.02em] text-white/80">
              {LEVEL[sekolah?.level] || sekolah?.level || ""}{sekolah?.npsn ? ` · NPSN ${sekolah.npsn}` : ""}
            </p>
          </div>
        </div>
        <p className="mt-[1.3mm] rounded-[1mm] bg-white/15 py-[0.8mm] text-center text-[6.2pt] font-extrabold uppercase leading-[2.5mm] tracking-[0.05em] text-white">
          {JUDUL[a.peran] || "Kartu Anggota"}
        </p>
      </header>

      <div className="flex shrink-0 flex-col items-center px-[2.4mm]">
        <div className="h-[17.5mm] w-[13.8mm] shrink-0 overflow-hidden rounded-[1.1mm] ring-[0.5mm] ring-white shadow-md">
          <FotoAnggota nama={a.nama} gender={a.gender} peran={a.peran} photo={a.photo}
            className="h-full w-full object-cover" testId={`${testId}-foto`} />
        </div>
        <p className="mt-[1.4mm] line-clamp-2 min-h-[6.4mm] text-center text-[8pt] font-extrabold uppercase leading-[3.2mm] tracking-tight">
          {a.nama || "—"}
        </p>
        <p className="mt-[0.2mm] text-[4.3pt] font-bold uppercase leading-[1.9mm] tracking-[0.08em] text-slate-500">
          {ROLE_LABEL[a.peran] || a.peran || ""}
        </p>
      </div>

      {dataKartu.length > 0 && (
        <div className="mx-[2.4mm] shrink-0 space-y-[0.4mm] border-t border-dashed border-slate-300 pt-[1.3mm]">
          {dataKartu.map(([label, nilai]) => <Baris key={label} label={label} nilai={nilai} />)}
        </div>
      )}

      <div className="shrink-0 px-[2.4mm] pt-[1.8mm] pb-[1.2mm]">
        <div className="flex items-stretch gap-[1.6mm] rounded-[1.2mm] bg-slate-100/80 p-[1.3mm]">
          <div className="flex shrink-0 flex-col items-center">
            {src
              ? <img src={src} alt={`Kode QR verifikasi ${a.nama || "kartu"}`} className="h-[13mm] w-[13mm] rounded-[0.7mm] bg-white p-[0.4mm]" data-testid={`${testId}-qr`} />
              : <div className="h-[13mm] w-[13mm] animate-pulse rounded-[0.7mm] bg-slate-300" />}
            <p className="mt-[0.4mm] text-[3.3pt] font-bold uppercase leading-[1.6mm] tracking-[0.04em]" style={{ color: gelap }}>
              Pindai untuk presensi
            </p>
          </div>
          <div className="min-w-0 flex-1 overflow-hidden text-[3.8pt] leading-[2mm] text-slate-600">
            <p className="truncate font-mono text-[5.2pt] font-bold leading-[2.4mm] tracking-tight text-slate-900">{kartu?.seri || kartu?.kunci}</p>
            <p className="mt-[0.3mm] font-bold leading-[2mm] text-slate-700">T.A. {kartu?.tahun_ajaran || "—"}</p>
            <p className="mt-[0.4mm] line-clamp-2 leading-[2mm]">{alamat || sekolah?.city || ""}</p>
            {sekolah?.phone && <p className="mt-[0.3mm] truncate leading-[2mm]">Telp. {sekolah.phone}</p>}
            {sekolah?.email && <p className="mt-[0.3mm] truncate leading-[2mm]">{sekolah.email}</p>}
          </div>
        </div>
      </div>

      <footer className="flex shrink-0 items-center justify-between gap-[1.5mm] px-[2.4mm] py-[1mm] text-[3.7pt] font-bold uppercase leading-[1.6mm] tracking-[0.05em] text-white"
        style={{ background: terang }}>
        <span className="truncate">{kartu?.status === "active" ? "Kartu aktif" : "Kartu tidak aktif"}</span>
        {sekolah?.city && <span className="truncate opacity-80">{sekolah.city}</span>}
        <SistLogo size="md" inverted tagline={false} />
      </footer>
    </div>
  );
};

export default KartuID;
